import { useEffect, useRef, useState } from "react";

// Manual timer. Never auto-starts. Until started, shows the duration only , 
// no clock the table did not start. Can be paused and reset. At zero it blocks
// nothing and shows one quiet line.
const format = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const AreaTimer = ({ label, durationMinutes, onStart, onTick, onReport }) => {
  const total = durationMinutes * 60;
  const [remaining, setRemaining] = useState(total);
  const [status, setStatus] = useState("idle"); // idle | running | paused | done
  const intervalRef = useRef(null);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  // Report state up so a collapsed parent (segment guide) can show a readout.
  useEffect(() => {
    if (onReport) onReport(label, { status, remaining });
  }, [status, remaining, label, onReport]);

  const run = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        const next = r <= 1 ? 0 : r - 1;
        if (r <= 1) {
          clearInterval(intervalRef.current);
          setStatus("done");
        }
        if (onTick) onTick(total - next);
        return next;
      });
    }, 1000);
  };

  const start = () => {
    if (status === "idle" && onStart) onStart();
    setStatus("running");
    run();
  };
  const pause = () => {
    clearInterval(intervalRef.current);
    setStatus("paused");
  };
  const reset = () => {
    clearInterval(intervalRef.current);
    setRemaining(total);
    setStatus("idle");
  };

  const btn =
    "px-4 py-2 text-sm text-center transition-colors duration-150 border border-ink/20 hover:border-orange hover:text-orange-dark";
  const btnPrimary =
    "px-4 py-2 text-sm text-center transition-colors duration-150 bg-orange hover:bg-orange-dark text-white";

  return (
    <div
      data-testid={`timer-${label}`}
      className="bg-white border border-sand p-5 min-w-0 box-border overflow-hidden"
    >
      <div className="flex items-center justify-between gap-2 mb-4 min-w-0">
        <span className="mono-label text-ink/60 truncate">{label}</span>
        <span className="mono-label text-ink/40 shrink-0">
          {durationMinutes} MINUTES
        </span>
      </div>

      {/* Readout, always on its own line */}
      <div className="mb-4 min-w-0">
        {status === "done" ? (
          <p data-testid={`timer-done-${label}`} className="text-sm text-ink/70">
            That is the time we planned. Finish your thought and move on when
            your host calls it.
          </p>
        ) : status === "running" || status === "paused" ? (
          <span
            data-testid={`timer-countdown-${label}`}
            className="block font-mono tabular-nums text-3xl leading-none text-ink"
          >
            {format(remaining)}
          </span>
        ) : (
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono tabular-nums text-3xl leading-none">
              {durationMinutes}
            </span>
            <span className="mono-label text-ink/60">minutes</span>
          </div>
        )}
      </div>

      {/* Control row, same reserved height in every state */}
      <div className="flex gap-2 min-w-0 min-h-[40px]">
        {status === "idle" && (
          <button
            data-testid={`timer-start-${label}`}
            className={`${btnPrimary} flex-1 min-w-0`}
            onClick={start}
          >
            Start
          </button>
        )}
        {status === "running" && (
          <>
            <button className={`${btn} flex-1 min-w-0`} onClick={pause}>
              Pause
            </button>
            <button className={`${btn} flex-1 min-w-0`} onClick={reset}>
              Reset
            </button>
          </>
        )}
        {status === "paused" && (
          <>
            <button className={`${btnPrimary} flex-1 min-w-0`} onClick={start}>
              Resume
            </button>
            <button className={`${btn} flex-1 min-w-0`} onClick={reset}>
              Reset
            </button>
          </>
        )}
        {status === "done" && (
          <button className={`${btn} flex-1 min-w-0`} onClick={reset}>
            Reset
          </button>
        )}
      </div>
    </div>
  );
};
